import React, { ReactNode } from 'react';
import type { Column } from 'react-table';
import { v4 as uuid } from 'uuid';
import EVerifyUser from '../../../../components/Enrollment/EVerifyUsers/data/EVerifyUser';
import { TableData } from '../../../../components/Enrollment/EVerifyUsers/data/EVerifyUsersTableData';
import EVerifyUsersForm from '../../../../components/Enrollment/EVerifyUsers/EVerifyUsersForm';
import SubmitAction from '../../../../components/Enrollment/SubmitAction';
import { useEnrollment } from '../../../../context/Enrollment/EnrollmentContext';
import { EnrollmentData, EnrollmentType } from '../../EnrollmentData';
import { removeEmptyStrings } from '../../../../utils/util';
import AlertConstants from '../../../../constants/AlertConstants';

export type BaseEVerifyUsersProps = {
  addButtonText: string;
  noRowsText: string;
  pageTitle?: string;
  primaryText: ReactNode;
  secondaryText?: string;
  columns: Column<TableData>[];
  isAddDisabled?: (userData: EVerifyUser[]) => boolean;
  checkUsersForError?: (users: EVerifyUser[]) => AlertConstants | undefined;
};

const BaseEVerifyUsers = ({
  addButtonText,
  noRowsText,
  pageTitle = 'E-Verify Users',
  primaryText,
  secondaryText,
  columns,
  isAddDisabled,
  checkUsersForError,
}: BaseEVerifyUsersProps): JSX.Element => {
  const { enrollmentData, nextPage, setEnrollmentData } = useEnrollment();

  const removeExistingMouSignatory = (user: EVerifyUser): EVerifyUser[] => {
    const updatedUsers = enrollmentData.everifyUserFormData || [];
    if (user.mouSignatory === 'Yes') {
      const indexOfMou = enrollmentData.everifyUserFormData?.findIndex(
        (everifyUser) => everifyUser.mouSignatory === 'Yes',
      );
      if (indexOfMou !== undefined && indexOfMou >= 0) {
        updatedUsers[indexOfMou] = {
          ...updatedUsers[indexOfMou],
          mouSignatory: 'No',
        };
      }
    }
    return updatedUsers;
  };

  const onAdd = (user: EVerifyUser) => {
    const updatedUsers = removeExistingMouSignatory(user);

    const userWithId = {
      id: uuid(),
      lastName: user.lastName,
      firstName: user.firstName,
      middleInitial: user.middleInitial,
      phoneNumber: user.phoneNumber,
      phoneNumberExtension: user.phoneNumberExtension,
      emailAddress: user.emailAddress,
      mouSignatory: user.mouSignatory,
      admin: user.admin,
    } as EVerifyUser;
    updatedUsers.push(userWithId);

    setEnrollmentData((prevState: EnrollmentData) => {
      return {
        ...prevState,
        everifyUserFormData: updatedUsers,
      };
    });
  };

  const onUpdate = (user: EVerifyUser) => {
    const index = enrollmentData.everifyUserFormData?.findIndex((everifyUser) => everifyUser.id === user.id) || 0;
    const updatedUsers = removeExistingMouSignatory(user);
    updatedUsers[index] = user;

    setEnrollmentData((prevState: EnrollmentData) => {
      return {
        ...prevState,
        everifyUserFormData: updatedUsers,
      };
    });
  };

  const onDelete = (user: EVerifyUser) => {
    setEnrollmentData((prevState: EnrollmentData) => {
      const updatedUsers = prevState.everifyUserFormData?.filter((everifyUser) => everifyUser.id !== user.id);
      return {
        ...prevState,
        everifyUserFormData: updatedUsers,
      };
    });
  };

  const onSubmit = (action: SubmitAction, user: EVerifyUser) => {
    removeEmptyStrings(user);
    switch (action) {
      case SubmitAction.ADD:
        onAdd(user);
        break;
      case SubmitAction.UPDATE:
        onUpdate(user);
        break;
      case SubmitAction.DELETE:
        onDelete(user);
        break;
      default:
        break;
    }
  };

  return (
    <section className="padding-y-4">
      <h1 className="margin-bottom-4">{pageTitle}</h1>
      {primaryText}
      <EVerifyUsersForm
        addButtonText={addButtonText}
        noRowsText={noRowsText}
        columns={columns}
        usersData={enrollmentData.everifyUserFormData || []}
        tableText={secondaryText}
        modalHeaderText={enrollmentData.enrollmentType === EnrollmentType.CLIENT ? 'Signatory' : undefined}
        onNext={nextPage}
        onSubmit={onSubmit}
        isAddDisabled={isAddDisabled}
        checkUsersForError={checkUsersForError}
      />
    </section>
  );
};

export default BaseEVerifyUsers;
